import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, Flame, AlertTriangle, Calendar, MapPin, Handshake, TrendingUp, Target,
  Brain, ArrowRight, Trophy, Sparkles, MessageCircle, Plus, Activity, Clock3, ChevronRight,
} from "lucide-react";
import { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatCard, Spinner } from "../components/common";
import { formatINR } from "../lib/helpers";
import NexusMotion from "../components/NexusMotion";

export default function Dashboard() {
  const { user, isAdmin } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [champion, setChampion] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);

  useEffect(() => {
    API.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    if (isAdmin) {
      API.get("/ai/daily-briefing").then((r) => setBriefing(r.data)).catch(() => {});
      API.get("/admin/competitions/champion/current").then((r) => setChampion(r.data)).catch(() => {});
    }
    const checkAI = async () => {
      try { const {data} = await API.get("/ai/status"); setAiStatus(data); }
      catch { try { const {data} = await API.get("/system/health"); setAiStatus(data.ai || {}); }
        catch { setAiStatus({unavailable:true}); } }
    };
    if (isAdmin) { checkAI(); const timer = setInterval(checkAI, 30000); return () => clearInterval(timer); }
  }, [isAdmin]);

  if (!data) return <Spinner />;

  const targetPct = Math.min(100, Math.round((data.revenue / data.monthly_target) * 100)) || 0;
  const firstName = user?.name?.split(" ")[0] || "there";
  const today = new Intl.DateTimeFormat("en-IN", { weekday: "long", day: "numeric", month: "long" }).format(new Date());

  return (
    <div className="dashboard-view">
      <section className="command-hero animate-fade-up" data-testid="dashboard-header">
        <div className="command-hero__glow" />
        <div className="command-hero__content">
          <div className="command-eyebrow"><Activity className="h-3.5 w-3.5" /> LIVE COMMAND CENTRE <span /> {today}</div>
          <h1>Good day, {firstName} <span>✦</span></h1>
          <p>{isAdmin ? "Your sales operation is live. Here is what needs your attention now." : "Your assigned pipeline and next best actions, in one place."}</p>
          <div className="command-actions">
            <button onClick={() => nav("/whatsapp/inbox")} className="command-btn command-btn--primary"><MessageCircle className="h-4 w-4" /> Open WhatsApp</button>
            <button onClick={() => nav("/leads")} className="command-btn"><Plus className="h-4 w-4" /> View leads</button>
          </div>
        </div>
        <div className="command-pulse">
          <NexusMotion variant="eye" size="normal" />
          <div><strong>{isAdmin ? (aiStatus?.unavailable ? "AI status unavailable" : aiStatus ? (aiStatus.cloud?.running || aiStatus.local?.running ? "AI system running" : "AI system paused") : "Checking AI status") : "Pipeline overview"}</strong><span>{data.total_leads} investor leads in the pipeline</span></div>
        </div>
      </section>

      <div className="dashboard-section-head"><div><span>Business pulse</span><h2>What is happening now</h2></div><button onClick={() => nav("/analytics")}>View analytics <ArrowRight className="h-4 w-4" /></button></div>

      {isAdmin && champion?.agent_id && <button onClick={() => nav(`/ai-agents/${champion.agent_id}`)} className="winner-banner mb-5 w-full text-left"><div className="winner-avatar">{champion.avatar || champion.agent_name?.[0]}</div><div><div className="caption-mono">CURRENT AI CHAMPION</div><div className="text-xl font-bold">{champion.agent_name} <span className="winner-tag">WINNER</span></div><div className="text-xs opacity-70">{champion.competition_name} · score {champion.score} · {champion.reward}</div></div><Trophy className="ml-auto text-amber-400" /></button>}

      <div className="dashboard-metrics grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
        <StatCard testid="stat-new-leads" label="New Leads" value={data.new_leads} icon={Users} accent="blue" sub={`${data.total_leads} total investors`} to="/leads" />
        <StatCard testid="stat-hot-leads" label="Hot Leads" value={data.hot_leads} icon={Flame} accent="red" sub="Awaiting action" to="/hot-leads" />
        <StatCard testid="stat-attention" label="Needs Attention" value={data.attention} icon={AlertTriangle} accent="amber" sub="Human intervention" to="/hot-leads?filter=attention" ariaLabel="Open human attention queue" />
        <StatCard testid="stat-associate-leads" label="Associate Leads" value={data.associate_leads} icon={Handshake} accent="emerald" sub="Partner pipeline" to="/leads?lead_type=ASSOCIATE" />
      </div>

      <div className="dashboard-metrics dashboard-metrics--secondary grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        <StatCard testid="stat-meetings" label="Meetings Today" value={data.meetings_today} icon={Calendar} accent="purple" to="/meetings" />
        <StatCard testid="stat-visits" label="Site Visits Today" value={data.site_visits_today} icon={MapPin} accent="blue" to="/site-visits" />
        <StatCard testid="stat-deals" label="Active Deals" value={data.deals} icon={Handshake} accent="emerald" sub={`Pipeline ${formatINR(data.pipeline_value)}`} to="/deals" />
        <StatCard testid="stat-revenue" label="Revenue (Won)" value={formatINR(data.revenue)} icon={TrendingUp} accent="amber" to="/reports" />
      </div>

      <div className="dashboard-section-head"><div><span>Intelligence layer</span><h2>Insights and next best actions</h2></div></div>
      <div className="dashboard-intelligence grid grid-cols-1 xl:grid-cols-12 gap-5">
        {isAdmin && briefing && (
          <div className="ai-brief-panel xl:col-span-7" data-testid="daily-briefing">
            <div className="panel-title"><div className="panel-icon"><Brain className="h-5 w-5" /></div><div><span>MB Nexus Intelligence</span><h3>Daily AI briefing</h3></div><div className="live-chip"><i />Live</div></div>
            <div className="ai-brief-copy whitespace-pre-line">{briefing.briefing}</div>
            <button onClick={() => nav("/ai-intelligence")}>Explore intelligence <ChevronRight className="h-4 w-4" /></button>
          </div>
        )}

        <div className={`target-panel ${isAdmin && briefing ? "xl:col-span-5" : "xl:col-span-12"}`} data-testid="monthly-target-card">
          <div className="panel-title"><div className="panel-icon panel-icon--gold"><Target className="h-5 w-5" /></div><div><span>Performance</span><h3>Monthly target</h3></div></div>
          <div className="target-layout">
            <div className="target-ring" style={{ "--progress": `${targetPct * 3.6}deg` }}><div><strong>{targetPct}%</strong><span>achieved</span></div></div>
            <div className="target-details"><strong>{formatINR(data.revenue)}</strong><span>of {formatINR(data.monthly_target)}</span><div className="target-bar"><i style={{ width: `${targetPct}%` }} /></div><small><Clock3 className="h-3.5 w-3.5" /> Keep momentum with timely follow-ups</small></div>
          </div>
        </div>

        {isAdmin && (
          <div className="recommend-panel xl:col-span-12" data-testid="ai-recommendations-card">
            <div className="panel-title"><div className="panel-icon panel-icon--cyan"><Sparkles className="h-5 w-5" /></div><div><span>Priority queue</span><h3>AI recommendations</h3></div><button onClick={() => nav("/hot-leads")}>Review queue <ArrowRight className="h-4 w-4" /></button></div>
            <div className="recommend-grid">
              {data.recommendations?.map((r) => <div key={r.id} className="recommend-item"><span>{r.confidence}%</span><div><strong>Recommended action</strong><p>{r.text}</p></div><ChevronRight className="h-4 w-4" /></div>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
